// src/admin/AdminMessages.jsx
import { useEffect, useState } from "react";
import { collection, getDocs, updateDoc, deleteDoc, doc, orderBy, query } from "../services/mysqlStore";
import { db } from "../services/mysqlStore";
import { apiRequest } from "../services/apiClient";

function ReplyModal({ message, onClose, onSent }) {
  var [replyText, setReplyText] = useState("");
  var [sending, setSending]     = useState(false);
  var [status, setStatus]       = useState(null); // "success" | "error"

  async function handleSend() {
    if (!replyText.trim()) return;
    setSending(true);
    setStatus(null);

    try {
      await apiRequest(`/admin/messages/${encodeURIComponent(message.id)}/reply`, {
        method: "POST",
        body: JSON.stringify({ replyText }),
      });
      setStatus("success");
      onSent?.();
      setTimeout(function() { onClose(); }, 2000);
    } catch (err) {
      setStatus(err.message || "Failed to send reply.");
    } finally {
      setSending(false);
    }
  }

  return (
    <div style={overlayStyle} onClick={function(e) { if (e.target === e.currentTarget) onClose(); }}>
      <div style={modalStyle}>

        {/* Modal Header */}
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
          <div>
            <h3 style={{ color: "#fff", fontWeight: 700, fontSize: 18, margin: 0 }}>✉️ Reply to {message.name}</h3>
            <p style={{ color: "rgba(255,255,255,0.4)", fontSize: 13, marginTop: 4 }}>{message.email}</p>
          </div>
          <button onClick={onClose} style={closeBtnStyle}>✕</button>
        </div>

        {/* Original Message */}
        <div style={originalMsgStyle}>
          <p style={{ color: "rgba(255,255,255,0.35)", fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 8 }}>
            Original Message
          </p>
          <p style={{ color: "rgba(255,255,255,0.55)", fontSize: 13, lineHeight: 1.6, whiteSpace: "pre-wrap" }}>
            {message.message}
          </p>
        </div>

        {/* Reply Textarea */}
        <div style={{ marginBottom: 20 }}>
          <label style={{ color: "rgba(255,255,255,0.6)", fontSize: 12, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.05em", display: "block", marginBottom: 8 }}>
            Your Reply
          </label>
          <textarea
            value={replyText}
            onChange={function(e) { setReplyText(e.target.value); }}
            placeholder={"Dear " + message.name + ",\n\nThank you for contacting Circuit Printing...\n\nBest regards,\nCircuit Printing Team"}
            rows={8}
            disabled={sending || status === "success"}
            style={textareaStyle}
          />
        </div>

        {/* Status Messages */}
        {status === "success" && (
          <div style={alertStyle("#4ade80", "rgba(74,222,128,0.1)", "rgba(74,222,128,0.2)")}>
            ✅ Reply sent successfully to {message.email}!
          </div>
        )}
        {status && status !== "success" && (
          <div style={alertStyle("#f87171", "rgba(248,113,113,0.1)", "rgba(248,113,113,0.2)")}>
            ❌ {status}
          </div>
        )}

        {/* Buttons */}
        <div style={{ display: "flex", gap: 12, justifyContent: "flex-end" }}>
          <button onClick={onClose} disabled={sending} style={cancelBtnStyle}>
            Cancel
          </button>
          <button
            onClick={handleSend}
            disabled={sending || !replyText.trim() || status === "success"}
            style={{
              ...sendBtnStyle,
              opacity: (sending || !replyText.trim() || status === "success") ? 0.5 : 1,
              cursor: (sending || !replyText.trim() || status === "success") ? "not-allowed" : "pointer"
            }}
          >
            {sending ? "Sending..." : "Send Reply →"}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function AdminMessages() {
  var [messages, setMessages]     = useState([]);
  var [selected, setSelected]     = useState(null);
  var [showReply, setShowReply]   = useState(false);

  async function fetchMessages() {
    var list;
    try {
      var q    = query(collection(db, "messages"), orderBy("createdAt", "desc"));
      var snap = await getDocs(q);
      list = snap.docs.map(function(d) { return { id: d.id, ...d.data() }; });
    } catch {
      var snap2 = await getDocs(collection(db, "messages"));
      list = snap2.docs.map(function(d) { return { id: d.id, ...d.data() }; });
    }
    setMessages(list);
    setSelected(function(prev) { return prev ? (list.find(function(m) { return m.id === prev.id; }) || prev) : prev; });
  }

  useEffect(function() { fetchMessages(); }, []);

  async function markRead(id) {
    await updateDoc(doc(db, "messages", id), { read: true });
    fetchMessages();
  }

  async function del(id) {
    if (!window.confirm("Delete this message?")) return;
    await deleteDoc(doc(db, "messages", id));
    setSelected(null);
    fetchMessages();
  }

  return (
    <>
      <div style={{ display: "flex", gap: 24, height: "calc(100vh - 140px)" }}>

        {/* Left: Message List */}
        <div className="adm-card" style={{ width: 320, overflowY: "auto", flexShrink: 0 }}>
          <div className="adm-card-header">
            <span className="adm-card-title">Messages ({messages.length})</span>
          </div>
          {messages.length === 0 && (
            <p style={{ padding: 24, color: "rgba(255,255,255,0.3)", fontSize: 13 }}>No messages yet.</p>
          )}
          {messages.map(function(m) {
            return (
              <div key={m.id}
                onClick={function() { setSelected(m); if (!m.read) markRead(m.id); }}
                style={{
                  padding: "14px 20px",
                  borderBottom: "1px solid rgba(255,255,255,0.06)",
                  cursor: "pointer",
                  background: selected?.id === m.id ? "rgba(0,170,221,0.1)" : "transparent",
                  borderLeft: m.read ? "3px solid transparent" : "3px solid var(--magenta)"
                }}
              >
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                  <strong style={{ color: "white", fontSize: 14 }}>{m.name}</strong>
                  <span style={{ display: "flex", gap: 6 }}>
                    {m.replies?.length > 0 && <span className="adm-badge adm-badge-done">Replied</span>}
                    {!m.read && <span className="adm-badge adm-badge-new">New</span>}
                  </span>
                </div>
                <p style={{ color: "rgba(255,255,255,0.45)", fontSize: 12, marginTop: 2 }}>{m.email}</p>
                <p style={{ color: "rgba(255,255,255,0.6)", fontSize: 13, marginTop: 4, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                  {m.message}
                </p>
              </div>
            );
          })}
        </div>

        {/* Right: Message Detail */}
        <div className="adm-card" style={{ flex: 1, overflowY: "auto" }}>
          {!selected ? (
            <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: "100%", color: "rgba(255,255,255,0.25)", flexDirection: "column", gap: 8 }}>
              <span style={{ fontSize: 48 }}>✉️</span>
              <p>Select a message to view</p>
            </div>
          ) : (
            <div style={{ padding: 28 }}>
              {/* Message Header */}
              <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 24 }}>
                <div>
                  <h2 style={{ color: "white", fontFamily: "var(--font-display)", fontSize: 22, fontWeight: 700 }}>
                    {selected.name}
                  </h2>
                  <p style={{ color: "rgba(255,255,255,0.45)", fontSize: 13, marginTop: 4 }}>
                    {selected.email}{selected.phone ? " · " + selected.phone : ""}
                  </p>
                  {selected.company && (
                    <p style={{ color: "var(--cyan)", fontSize: 13, marginTop: 2 }}>{selected.company}</p>
                  )}
                </div>
                <button className="adm-btn adm-btn-sm adm-btn-danger" onClick={function() { del(selected.id); }}>
                  Delete
                </button>
              </div>

              {/* Subject Badge */}
              {selected.subject && (
                <div style={{ marginBottom: 16 }}>
                  <span className="adm-badge adm-badge-done">{selected.subject}</span>
                </div>
              )}

              {/* Message Body */}
              <div style={{ background: "rgba(255,255,255,0.04)", borderRadius: 8, padding: 20, color: "rgba(255,255,255,0.8)", lineHeight: 1.7, whiteSpace: "pre-wrap", marginBottom: 24 }}>
                {selected.message}
              </div>

              {/* Reply History */}
              {selected.replies?.length > 0 && (
                <div style={{ marginBottom: 24 }}>
                  <p style={{ color: "rgba(255,255,255,0.35)", fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 8 }}>
                    Reply History ({selected.replies.length})
                  </p>
                  <div style={{ display: "grid", gap: 10 }}>
                    {selected.replies.map(function(r, i) {
                      return (
                        <div key={i} style={{ background: "rgba(0,170,221,0.08)", border: "1px solid rgba(0,170,221,0.15)", borderRadius: 8, padding: 14 }}>
                          <p style={{ color: "rgba(255,255,255,0.35)", fontSize: 11, marginBottom: 6 }}>{new Date(r.sentAt).toLocaleString()}</p>
                          <p style={{ color: "rgba(255,255,255,0.8)", fontSize: 13, lineHeight: 1.6, whiteSpace: "pre-wrap", margin: 0 }}>{r.text}</p>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Action Buttons */}
              <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
                <button
                  onClick={function() { setShowReply(true); }}
                  className="adm-btn adm-btn-primary"
                  style={{ display: "inline-flex", alignItems: "center", gap: 8 }}
                >
                  ✉️ Reply via Email
                </button>
                <a
                  href={"mailto:" + selected.email}
                  className="adm-btn"
                  style={{ display: "inline-flex", alignItems: "center", gap: 8, textDecoration: "none", background: "rgba(255,255,255,0.08)", color: "rgba(255,255,255,0.7)", padding: "8px 18px", borderRadius: 6, fontSize: 13, border: "1px solid rgba(255,255,255,0.1)" }}
                >
                  🔗 Open in Mail App
                </a>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Reply Modal */}
      {showReply && selected && (
        <ReplyModal
          message={selected}
          onClose={function() { setShowReply(false); }}
          onSent={fetchMessages}
        />
      )}
    </>
  );
}

// ── Styles ──
var overlayStyle = {
  position: "fixed", inset: 0,
  background: "rgba(0,0,0,0.7)",
  backdropFilter: "blur(4px)",
  zIndex: 9999,
  display: "flex", alignItems: "center", justifyContent: "center",
  padding: 20
};

var modalStyle = {
  background: "#1a1a2e",
  border: "1px solid rgba(255,255,255,0.1)",
  borderRadius: 16,
  padding: 28,
  width: "100%",
  maxWidth: 560,
  maxHeight: "90vh",
  overflowY: "auto",
  boxShadow: "0 24px 60px rgba(0,0,0,0.5)"
};

var closeBtnStyle = {
  background: "rgba(255,255,255,0.08)",
  border: "none",
  color: "rgba(255,255,255,0.6)",
  width: 32, height: 32,
  borderRadius: 8,
  cursor: "pointer",
  fontSize: 16,
  display: "flex", alignItems: "center", justifyContent: "center"
};

var originalMsgStyle = {
  background: "rgba(255,255,255,0.04)",
  border: "1px solid rgba(255,255,255,0.08)",
  borderRadius: 8,
  padding: "14px 16px",
  marginBottom: 20
};

var textareaStyle = {
  width: "100%",
  background: "rgba(255,255,255,0.06)",
  border: "1px solid rgba(255,255,255,0.12)",
  borderRadius: 8,
  padding: "12px 14px",
  color: "#fff",
  fontSize: 14,
  lineHeight: 1.6,
  resize: "vertical",
  fontFamily: "inherit",
  boxSizing: "border-box",
  outline: "none"
};

var cancelBtnStyle = {
  background: "rgba(255,255,255,0.08)",
  border: "1px solid rgba(255,255,255,0.1)",
  color: "rgba(255,255,255,0.6)",
  padding: "10px 20px",
  borderRadius: 8,
  cursor: "pointer",
  fontSize: 14,
  fontWeight: 600
};

var sendBtnStyle = {
  background: "var(--cyan)",
  border: "none",
  color: "#fff",
  padding: "10px 24px",
  borderRadius: 8,
  fontSize: 14,
  fontWeight: 700,
  cursor: "pointer",
  transition: "background 0.2s"
};

function alertStyle(color, bg, border) {
  return {
    background: bg,
    border: "1px solid " + border,
    color: color,
    padding: "10px 16px",
    borderRadius: 8,
    fontSize: 13,
    marginBottom: 16,
    fontWeight: 600
  };
}
